import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { reduce } from 'lodash/fp';

import { createReducer } from '@mews/framework/redux';

import { BOOKING_CLEARED, BOOKING_RESTARTED } from '../../global/actionTypes';
import { RESOURCE_ORDER_ADDED } from '../../resourceOrder/actionTypes';
import {
    HourlyPromotedServiceReservationChangePayload,
    PromotedServiceReservationCountChangePayload,
    RemovePromotedServiceReservationPayload,
} from '../../resourceOrder/payloadTypes';
import {
    HOURLY_PROMOTED_SERVICE_RESERVATION_CHANGED,
    PROMOTED_SERVICE_RESERVATION_COUNT_CHANGED,
    PROMOTED_SERVICE_RESERVATION_REMOVED,
    RESERVATION_COUNT_CHANGED,
} from './actionTypes';
import { Reservation, Reservations } from './types';

const INITIAL_STATE = {};

const addReservations = (previousReservations: Reservations, { reservations }: { reservations: Reservations }) =>
    reduce(
        (acc, { orderId, promotedServices, ...otherReservation }) => {
            const previousCount = acc[orderId]?.count ?? 0;
            const previousPromotedServices = acc[orderId]?.promotedServices ?? [];

            // As we bundle identical reservations, e.g. with the same occupancies -
            // 2 rooms for 2 adults and 1 room for 1 adult. We need to bundle promoted
            // services as well. If we have 3 reservations and 2 identical and 3 parking
            // slots reservations -> we should display one reservation for 2 rooms and one
            // reservation for 2 parking slots and one reservation for one room and one
            // reservation for one parking slot.
            const calculatedPromotedServices = pipe(
                array.isNonEmpty(previousPromotedServices) ? previousPromotedServices : promotedServices,
                array.map((promotedService) => {
                    const services = array.isNonEmpty(previousPromotedServices)
                        ? promotedServices
                        : previousPromotedServices;
                    return services.some((service) => service.reservationId === promotedService.reservationId)
                        ? {
                              ...promotedService,
                              count: promotedService.count + 1,
                          }
                        : promotedService;
                })
            );

            return {
                ...acc,
                [orderId]: {
                    orderId,
                    ...otherReservation,
                    promotedServices:
                        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                        previousReservations[orderId] &&
                        array.isNonEmpty(previousReservations[orderId].promotedServices)
                            ? previousReservations[orderId].promotedServices
                            : calculatedPromotedServices,
                    count: previousCount + 1,
                },
            };
        },
        previousReservations,
        reservations
    );

const changeReservationCount = (
    state: Reservations,
    { reservation, countChange }: { reservation: Reservation; countChange: number }
) => {
    const currentReservation = state[reservation.orderId];

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (!currentReservation) {
        return state;
    }
    currentReservation.count += countChange;

    if (currentReservation.count <= 0) {
        return record.deleteAt(reservation.orderId)(state);
    }

    return {
        ...state,
        [reservation.orderId]: currentReservation,
    };
};

const changePromotedServiceReservationCount = (
    state: Reservations,
    { orderId, promotedServiceId, countChange }: PromotedServiceReservationCountChangePayload
) => {
    const currentReservation = state[orderId];
    const promotedServices = pipe(
        state[orderId].promotedServices,
        array.filterMap((promotedServiceReservation) => {
            if (promotedServiceReservation.id === promotedServiceId) {
                const count = promotedServiceReservation.count + countChange;
                if (count === 0) {
                    return option.none;
                }
                return option.some({ ...promotedServiceReservation, count });
            }
            return option.some(promotedServiceReservation);
        })
    );
    return {
        ...state,
        [orderId]: { ...currentReservation, promotedServices },
    };
};

const updateHourlyPromotedServiceReservation = (
    state: Reservations,
    { orderId, promotedServiceId, startUtc, endUtc, count }: HourlyPromotedServiceReservationChangePayload
) => {
    const currentReservation = state[orderId];
    const promotedServices = pipe(
        state[orderId].promotedServices,
        array.map((promotedServiceReservation) => {
            if (promotedServiceReservation.id === promotedServiceId) {
                return { ...promotedServiceReservation, startUtc, endUtc, count };
            }
            return promotedServiceReservation;
        })
    );
    return {
        ...state,
        [orderId]: { ...currentReservation, promotedServices },
    };
};

const removePromotedServiceReservation = (
    state: Reservations,
    { orderId, promotedServiceId }: RemovePromotedServiceReservationPayload
) => {
    const currentReservation = state[orderId];
    const promotedServices = pipe(
        state[orderId].promotedServices,
        array.filterMap((promotedServiceReservation) => {
            if (promotedServiceReservation.id === promotedServiceId) {
                return option.none;
            }
            return option.some(promotedServiceReservation);
        })
    );
    return {
        ...state,
        [orderId]: { ...currentReservation, promotedServices },
    };
};

export const reservations = createReducer(INITIAL_STATE, {
    [RESOURCE_ORDER_ADDED]: addReservations,

    [RESERVATION_COUNT_CHANGED]: changeReservationCount,

    [PROMOTED_SERVICE_RESERVATION_COUNT_CHANGED]: changePromotedServiceReservationCount,
    [HOURLY_PROMOTED_SERVICE_RESERVATION_CHANGED]: updateHourlyPromotedServiceReservation,

    [PROMOTED_SERVICE_RESERVATION_REMOVED]: removePromotedServiceReservation,

    [BOOKING_CLEARED]: () => INITIAL_STATE,

    [BOOKING_RESTARTED]: () => INITIAL_STATE,
});
